/**
 * Route scope classification for the admin panel.
 *
 * - Platform routes show cross-tenant data and do NOT require an active tenant.
 * - Account routes (profile/security) are user-level and need no tenant.
 * - Everything else is tenant-scoped: it requires an active tenant, and its API
 *   calls send the `x-tenant-id` header.
 *
 * Used by the tenant gate (don't render tenant pages until a tenant is selected)
 * and by the header (hide the tenant switcher + show a "Platform" badge on
 * platform routes).
 */

/** Platform (super-admin / cross-tenant) routes. `/admin` = the Dashboard. */
const PLATFORM_ROUTES = [
  '/admin',
  '/admin/tenants',
  '/admin/platform-users',
  '/admin/platform-roles',
  '/admin/platform-audit',
  '/admin/platform-billing',
  '/admin/platform-plans',
  '/admin/platform-tenant-billing',
  '/admin/platform-invoices',
  '/admin/voice-engines',
  '/admin/telephony-countries',
  '/admin/porting-requests',
] as const;

/** User-account routes — no tenant needed. */
const ACCOUNT_ROUTES = ['/admin/profile', '/admin/security'] as const;

function matches(pathname: string, route: string): boolean {
  // `/admin` must match exactly (it's the Dashboard, and the prefix of all
  // admin routes); others match the path or any sub-path.
  if (route === '/admin') return pathname === '/admin';
  return pathname === route || pathname.startsWith(`${route}/`);
}

export function isPlatformRoute(pathname: string): boolean {
  return PLATFORM_ROUTES.some((route) => matches(pathname, route));
}

export function isAccountRoute(pathname: string): boolean {
  return ACCOUNT_ROUTES.some((route) => matches(pathname, route));
}

/** True when the route needs an active tenant to function. */
export function routeNeedsTenant(pathname: string): boolean {
  return !isPlatformRoute(pathname) && !isAccountRoute(pathname);
}
